import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Stack, Box, Button, Container, InputAdornment, Typography, IconButton, Alert} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {AutoFillAwareTextField} from '../base';
import {mainLinearGradient} from '../../../theme/gradients';
import {AuthHeading, AuthLogo} from '../base';
import {useNavigate, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {postPasswordReset, getPasswordResetValidity} from '../../../api/auth';
import {delay} from '../../../utils/miscellaneous';
import {AnimationLoadingScreenWithProgressBar} from '../../../components/loading/loadingScreen';

/**
 * Component for changing the user's password.
 *
 * @param {Object} match - The match object from React Router.
 * @returns {JSX.Element} - The rendered ChangePasswordPage component.
 */
export const ChangePasswordPage = match => {
  const navigate = useNavigate();

  const {token} = useParams();

  const [password, setPassword] = useState('');
  const [retypedPassword, setRetypedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const [incorrect, setIncorrect] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [responseReturned, setResponseReturned] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Clear password, and confirm password textfields
      setPassword('');
      setRetypedPassword('');

      //Check if token is valid, if token is not valid then redirect user to login page
      getPasswordResetValidity(token)
        .then(req => {
          const data = req.data;
          if (data.result !== 'ok') {
            navigate('/start');
            return;
          }
          setResponseReturned(true);
          delay(2).then(() => {
            setIsLoading(false);
          });
        })
        .catch(error => {
          if (error.response.request.status === 401) navigate('/start');
        });
    };

    fetchData();
  }, []);

  const handleMouseDownPassword = e => {
    e.preventDefault();
  };

  const handlePasswordReset = () => {
    if (password !== retypedPassword) {
      setIncorrect(true);
      setErrorMessage('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setIncorrect(true);
      setErrorMessage('Password length should exceed 7 characters');
      return;
    }

    postPasswordReset(token, password)
      .catch(error => {
        setIncorrect(true);
        setErrorMessage('Something went wrong. Please try again.');
        // Clear password, and confirm password textfields
        setPassword('');
        setRetypedPassword('');
      })
      .then(res => {
        if (res.status == 200) {
          setIncorrect(false);
          setErrorMessage('');

          //Show confirmation message
          setPasswordResetSuccess(true);

          // Delay a couple of seconds so that user has the chance to read success alert message
          delay(5).then(() => {
            //Redirect to login page
            navigate('/start');
          });
        }
      });
  };

  return isLoading ? (
    <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={30} isAlmostComplete={responseReturned} />
  ) : (
    <Container maxWidth="sm">
      <Stack spacing={2} alignItems="start" justifyContent="center" sx={{height: '100vh'}}>
        <AuthLogo />
        <AuthHeading>Change your password</AuthHeading>
        <Typography color="darkgray">
          Double-check your new password for accuracy. We want to ensure you can access your account seamlessly.
        </Typography>
        {incorrect ? (
          <Alert
            severity="error"
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              marginX: 'auto',
            }}
          >
            {errorMessage}
          </Alert>
        ) : (
          ''
        )}
        {passwordResetSuccess ? (
          <Alert
            severity="success"
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              marginX: 'auto',
            }}
          >
            Your password was reset successfully!
          </Alert>
        ) : (
          ''
        )}
        <Box
          sx={{
            bgcolor: 'grayscale.light',
            border: '1px solid lightgray',
            borderRadius: 5,
            p: 3,
            boxSizing: 'border-box',
            width: 1,
          }}
        >
          <Stack spacing={2}>
            <AutoFillAwareTextField
              name="new-password-1"
              autoComplete="new-password-1"
              label="New Password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => {
                        setShowPassword(val => !val);
                      }}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              fullWidth
              type={showPassword ? 'text' : 'password'}
            />
            <AutoFillAwareTextField
              name="new-password-2"
              autoComplete="new-password-2"
              label="Confirm Password"
              helperText={'Minimum length: 8 characters'}
              value={retypedPassword}
              onChange={e => {
                setRetypedPassword(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => {
                        setShowConfirmedPassword(val => !val);
                      }}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmedPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              fullWidth
              type={showConfirmedPassword ? 'text' : 'password'}
            />
            <Button
              onClick={handlePasswordReset}
              variant="contained"
              disableElevation
              size="large"
              sx={{color: 'white', background: mainLinearGradient}}
              endIcon={<ArrowForwardIcon />}
            >
              Change your password
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
